<div class="modal fade" id="paymentModal" msEscClose
	(escPressed)="onCancel()"
	data-bs-backdrop="static" data-bs-keyboard="false" tabindex="-1"
	aria-labelledby="paymentModalLabel" aria-hidden="true">
	<div class="modal-lg modal-dialog modal-dialog-centered modal-dialog-scrollable modal-fullscreen-sm-down">
		<div class="modal-content">

			<!-- Header -->
			<div class="modal-header border-0 p-4">
				<h5 class="modal-title" id="paymentModalLabel">
					@switch (currentStep()) {
						@case (ModalStep.SelectMethod) { Select Payment Method }
						@case (ModalStep.SelectType) { Add Payment Method }
						@case (ModalStep.AddCard) { Add Credit Card }
						@case (ModalStep.OneTimePayForm) { Make a Payment }
						@case (ModalStep.Preview) { Make a Payment }
						@case (ModalStep.Processing) { Processing Payment }
						@case (ModalStep.Confirmation) { Payment Complete }
						@case (ModalStep.Error) { Payment Error }
					}
				</h5>
			</div>

			<!-- Body -->
			<div class="modal-body">

				<!-- SelectMethod: Pick saved method or "Add New" -->
				@if (currentStep() === ModalStep.SelectMethod) {
					@if (isLoading()) {
						<div class="d-flex justify-content-center align-items-center p-4">
							<div class="spinner-border text-primary" role="status">
								<span class="visually-hidden">Loading...</span>
							</div>
							<span class="ms-3">Loading payment methods...</span>
						</div>
					}

					@if (errorMessage() && !isLoading()) {
						<div class="alert alert-danger" role="alert">
							<fa-duotone-icon icon="circle-exclamation" secondaryOpacity="0.9" class="me-2" />
							{{ errorMessage() }}
						</div>
					}

					@if (!isLoading()) {
						@if (paymentMethods().length === 0 && !errorMessage()) {
							<div class="text-center text-muted py-4">
								<fa-duotone-icon icon="credit-card" secondaryOpacity="0.9" class="fs-1 d-block mb-2" />
								<p class="mb-0">No payment methods on file.</p>
								<p class="small">Add a payment method to continue.</p>
							</div>
						}

						<div role="listbox" aria-label="Saved payment methods">
							@for (method of paymentMethods(); track method.id; let i = $index) {
								<div class="card mb-2 cursor-pointer payment-method-option"
									role="option"
									tabindex="0"
									[class.border-primary]="selectedMethod()?.id === method.id"
									[class.selected]="selectedMethod()?.id === method.id"
									[attr.aria-selected]="selectedMethod()?.id === method.id"
									(click)="selectMethod(method)"
									(keydown.enter)="selectMethod(method)"
									(keydown.space)="$event.preventDefault(); selectMethod(method)">
									<div class="card-body d-flex align-items-center py-2">
										<div class="form-check me-3 mb-0">
											<input class="form-check-input" type="radio" name="paymentMethod"
												tabindex="-1"
												aria-hidden="true"
												[checked]="selectedMethod()?.id === method.id">
										</div>
										<fa-icon [icon]="getMethodIcon(method)" class="fs-4 me-3 text-primary"></fa-icon>
										<div>
											<div class="fw-semibold">{{ getMethodDisplayName(method) }}</div>
											@if (method.paymentMethodType === PaymentMethodType.Card && method.card) {
												<small class="text-muted">Expires {{ getMethodExpiry(method) }}</small>
											}
											@if (method.paymentMethodType === PaymentMethodType.AchDebit && method.usBankAccount) {
												<small class="text-muted">Bank account</small>
											}
										</div>
									</div>
								</div>
							}
						</div>
					}
				}

				<!-- SelectType: Card vs Bank + "Pay without saving" checkbox -->
				@if (currentStep() === ModalStep.SelectType) {
					<button type="button" class="card mb-2 cursor-pointer payment-method-option w-100 text-start border bg-transparent p-0"
						(click)="addAndSaveCard()">
						<div class="card-body d-flex justify-content-between align-items-center py-2">
							<div class="d-flex align-items-center">
								<fa-duotone-icon icon="credit-card" secondaryOpacity="0.9" class="fs-4 me-3 text-primary" />
								<div>
									<div class="fw-semibold">Add & Save Card</div>
									<small class="text-muted">Save a credit card for this and future payments</small>
								</div>
							</div>
							<fa-duotone-icon icon="chevron-right" secondaryOpacity="0.9" class="text-muted" />
						</div>
					</button>

					<button type="button" class="card mb-2 cursor-pointer payment-method-option w-100 text-start border bg-transparent p-0"
						(click)="payOnceWithCard()">
						<div class="card-body d-flex justify-content-between align-items-center py-2">
							<div class="d-flex align-items-center">
								<fa-duotone-icon icon="credit-card" secondaryOpacity="0.9" class="fs-4 me-3 text-primary" />
								<div>
									<div class="fw-semibold">Pay Once Without Saving</div>
									<small class="text-muted">Enter card details for a one-time payment</small>
								</div>
							</div>
							<fa-duotone-icon icon="chevron-right" secondaryOpacity="0.9" class="text-muted" />
						</div>
					</button>

					<button type="button" class="card mb-2 cursor-pointer payment-method-option w-100 text-start border bg-transparent p-0"
						(click)="goToPaymentSettings()">
						<div class="card-body d-flex justify-content-between align-items-center py-2">
							<div class="d-flex align-items-center">
								<fa-duotone-icon icon="building-columns" secondaryOpacity="0.9" class="fs-4 me-3 text-primary" />
								<div>
									<div class="fw-semibold">Bank Account (ACH)</div>
									<small class="text-muted">Add a bank account in your profile settings to pay via ACH</small>
								</div>
							</div>
							<fa-duotone-icon icon="arrow-up-right-from-square" secondaryOpacity="0.9" class="text-muted" />
						</div>
					</button>
				}

				<!-- AddCard: Stripe save-card form -->
				@if (currentStep() === ModalStep.AddCard) {
					<ms-stripe-add-credit-card
						#addCreditCardComponent
						[userId]="userId()"
						(paymentMethodSaved)="onPaymentMethodSaved()"
						(cancelled)="onAddFormCancelled()">
					</ms-stripe-add-credit-card>
				}

				<!-- OneTimePayForm: Stripe Payment Element loading state -->
				@if (currentStep() === ModalStep.OneTimePayForm && !isPaymentElementReady()) {
					<div class="payment-element-loading d-flex align-items-center justify-content-center">
						<div class="text-center">
							<div class="spinner-border text-primary" role="status">
								<span class="visually-hidden">Loading...</span>
							</div>
							<p class="mt-3 text-muted mb-0">{{ PaymentMessagingConstants.PAYMENT_LOADING_FORM() }}</p>
						</div>
					</div>
				}

				<!-- Stripe Payment Element container — kept in DOM across steps so confirmPayment() can read card data -->
				<div #paymentElementContainer
					[class.d-none]="currentStep() !== ModalStep.OneTimePayForm || !isPaymentElementReady()">
				</div>

				<!-- Preview: Itemized invoice table -->
				@if (currentStep() === ModalStep.Preview) {
					@if (isLoadingPreview()) {
						<div class="text-center py-4">
							<div class="spinner-border text-primary" role="status">
								<span class="visually-hidden">Loading...</span>
							</div>
							<p class="mt-3 text-muted">Loading payment preview...</p>
						</div>
					} @else if (preview()) {
						<div class="d-flex flex-column h-100">
							<div class="d-flex alert alert-primary align-items-center mb-3 p-3 rounded">
								<fa-icon [icon]="getActivePaymentIcon()" class="me-2 fs-5"></fa-icon>
								<span class="fw-medium">{{ getActivePaymentDisplayName() }}</span>
							</div>

							<div class="flex-grow-1 overflow-auto">
								<div class="table-responsive d-none d-sm-block">
									<table class="table table-sm mb-0">
										<thead>
											<tr>
												<th>Invoice</th>
												@if (hasItemLateFees()) {
													<th class="text-end">Late Fees</th>
												}
												<th class="text-end">Amount</th>
											</tr>
										</thead>
										<tbody>
											@for (item of preview()!.items; track item.propertyChargeId) {
												<tr>
													<td class="text-truncate invoice-name-cell" [msTooltip]="item.name ?? ''" msTooltipOnlyIfTruncated>{{ item.name }}</td>
													@if (hasItemLateFees()) {
														<td class="text-end">
															{{ item.lateFees | CurrencyDisplay: preview()!.currency }}
														</td>
													}
													<td class="text-end">{{ item.amount | CurrencyDisplay: preview()!.currency }}</td>
												</tr>
											}
										</tbody>
									</table>
								</div>

								<div class="d-block d-sm-none">
									@for (item of preview()!.items; track item.propertyChargeId) {
										<div class="card shadow-none rounded-1 mb-2">
											<div class="card-body py-2">
												<div class="fw-bold">{{ item.name }}</div>
												<div class="d-flex">
													@if (item.lateFees) {
														<span class="text-muted"><span class="fw-semibold">Late Fees:</span> {{ item.lateFees | CurrencyDisplay: preview()!.currency }}</span>
													}
													<span class="text-muted ms-auto">{{ item.amount | CurrencyDisplay: preview()!.currency }}</span>
												</div>
											</div>
										</div>
									}
								</div>
							</div>

							<div class="d-flex justify-content-end mt-1">
								<div class="payment-preview-totals">
									<div class="d-flex justify-content-between mt-2">
										<span class="fw-medium">Subtotal</span>
										<span class="fw-medium pe-3">{{ preview()!.subtotalCharges | CurrencyDisplay: preview()!.currency }}</span>
									</div>
									@if (preview()!.subtotalLateFees > 0) {
										<div class="d-flex justify-content-between mt-2">
											<span class="fw-medium">Late Fees</span>
											<span class="fw-medium pe-3">{{ preview()!.subtotalLateFees | CurrencyDisplay: preview()!.currency }}</span>
										</div>
									}
									<div class="d-flex justify-content-between mt-2">
										<span class="fw-medium">Service Fee</span>
										<span class="fw-medium pe-3">{{ preview()!.subtotalServiceFees | CurrencyDisplay: preview()!.currency }}</span>
									</div>
									<hr class="my-2">
									<div class="d-flex justify-content-between">
										<span class="text-primary fw-bold fs-6">Total</span>
										<span class="text-primary fw-bold fs-6 pe-3">{{ preview()!.grandTotal | CurrencyDisplay: preview()!.currency }}</span>
									</div>
								</div>
							</div>
						</div>
					}
				}

				<!-- Processing -->
				@if (currentStep() === ModalStep.Processing) {
					<div class="text-center py-5">
						<div class="spinner-border text-primary mb-3" role="status">
							<span class="visually-hidden">Processing...</span>
						</div>
						<p class="text-muted mb-0">Processing your payment...</p>
						<p class="text-muted small">Please do not close this window.</p>
					</div>
				}

				<!-- Confirmation -->
				@if (currentStep() === ModalStep.Confirmation && paymentIntent()) {
					<div class="d-flex align-items-center flex-column text-center py-4">
						<fa-duotone-icon icon="circle-check" class="info-icon text-success" />
						<p class="mt-2 mb-3">{{ getConfirmationMessage() }}</p>

						<div class="d-flex justify-content-center align-items-center gap-2 mb-2">
							<span class="fw-medium">Status:</span>
							<span class="badge rounded-pill" [ngClass]="getStatusBadgeClass()">
								{{ paymentIntent()!.status }}
							</span>
						</div>

						<div class="d-flex alert alert-primary w-100 w-md-50 justify-content-center align-items-center gap-2 mt-2">
							<fa-icon [icon]="getActivePaymentIcon()" class="me-1"></fa-icon>
							<span>{{ getActivePaymentDisplayName() }}</span>
						</div>
					</div>
				}

				<!-- Error -->
				@if (currentStep() === ModalStep.Error) {
					<div class="text-center py-4">
						<fa-duotone-icon icon="circle-xmark" class="info-icon text-danger" />
						<p class="mt-2 text-danger">{{ errorMessage() }}</p>
					</div>
				}

			</div>

			<!-- Footer -->
			@switch (currentStep()) {
				@case (ModalStep.SelectMethod) {
					@if (!isLoading()) {
						<div class="modal-footer border-0 d-flex flex-column flex-sm-row justify-content-between gap-2 pb-4">
							<button type="button" class="btn btn-ms-secondary w-100 w-sm-auto order-2 order-sm-0" (click)="showAddNewStep()">
								<fa-duotone-icon icon="plus" secondaryOpacity="0.9" class="me-1" />
								Add New
							</button>
							<div class="d-flex gap-2 w-100 w-sm-auto order-1 order-sm-0">
								<button type="button" class="btn btn-ms-secondary flex-grow-1 flex-sm-grow-0" (click)="onCancel()">Cancel</button>
								<button type="button" class="btn btn-success flex-grow-1 flex-sm-grow-0" [disabled]="!selectedMethod()" (click)="payWithSelectedMethod()">
									Pay with This Method
								</button>
							</div>
						</div>
					}
				}
				@case (ModalStep.SelectType) {
					<div class="modal-footer border-0 d-flex justify-content-center pb-4">
						<button type="button" class="btn btn-ms-secondary" (click)="goBack()">Back</button>
					</div>
				}
				@case (ModalStep.OneTimePayForm) {
					<div class="modal-footer border-0 pb-4">
						<button type="button" class="btn btn-ms-secondary" (click)="goBack()">Back</button>
						<button type="button" class="btn btn-success"
							[disabled]="!isPaymentElementReady()"
							(click)="continueToPreview()">
							Continue to preview
						</button>
					</div>
				}
				@case (ModalStep.Preview) {
					<div class="modal-footer border-0 pb-4">
						<button type="button" class="btn btn-ms-secondary" (click)="goBack()">Back</button>
						<button type="button" class="btn btn-success"
							[disabled]="isLoadingPreview() || !preview()"
							(click)="confirmPayment()">
							Confirm Payment
						</button>
					</div>
				}
				@case (ModalStep.Confirmation) {
					<div class="modal-footer border-0 d-flex justify-content-center pb-4">
						<button type="button" class="btn btn-success" (click)="onDone()">Return to Invoices</button>
					</div>
				}
				@case (ModalStep.Error) {
					<div class="modal-footer border-0 pb-4">
						<button type="button" class="btn btn-ms-secondary" (click)="goBack()">Back</button>
						<button type="button" class="btn btn-primary" (click)="retryPayment()">Try Again</button>
					</div>
				}
			}

		</div>
	</div>
</div>

<ms-confirmation-modal
	#escConfirmationModalRef
	modalId="paymentEscConfirmationModal"
	confirmButtonText="Leave"
	cancelButtonText="Stay"
	(confirmed)="onEscConfirmed()"
	(cancelled)="onEscCancelled()"
/>
